import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, AlertTriangle, Package } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { api, fmtErr } from "../../lib/api";

export default function InventoryTab({ restaurant }) {
  const [ingredients, setIngredients] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [menus, setMenus] = useState([]);
  const [ingForm, setIngForm] = useState({ name: "", unit: "pcs", stock_level: "0", par_level: "0", cost_per_unit: "0" });
  const [recipeItem, setRecipeItem] = useState("");
  const [recipeIngs, setRecipeIngs] = useState([]);

  const load = () => {
    api.get(`/restaurants/${restaurant.id}/ingredients`).then((r) => setIngredients(r.data)).catch(() => {});
    api.get(`/restaurants/${restaurant.id}/recipes`).then((r) => setRecipes(r.data)).catch(() => {});
    api.get(`/restaurants/${restaurant.id}/menus`).then((r) => setMenus(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [restaurant.id]);

  const menuItems = menus.flatMap((m) => m.sections.flatMap((s) => s.items.map((i) => i.name)));

  const addIngredient = async () => {
    try {
      await api.post(`/restaurants/${restaurant.id}/ingredients`, {
        ...ingForm, stock_level: Number(ingForm.stock_level), par_level: Number(ingForm.par_level),
        cost_per_unit: Number(ingForm.cost_per_unit),
      });
      toast.success("Ingredient added");
      setIngForm({ name: "", unit: "pcs", stock_level: "0", par_level: "0", cost_per_unit: "0" });
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const adjustStock = async (iid, delta) => {
    const ing = ingredients.find((i) => i.id === iid);
    try {
      await api.patch(`/restaurants/${restaurant.id}/ingredients/${iid}`, { stock_level: Math.max(0, ing.stock_level + delta) });
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const removeIngredient = async (iid) => {
    try {
      await api.delete(`/restaurants/${restaurant.id}/ingredients/${iid}`);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const loadRecipe = (itemName) => {
    setRecipeItem(itemName);
    const r = recipes.find((x) => x.item_name === itemName);
    setRecipeIngs(r ? r.ingredients.map((i) => ({ ...i })) : []);
  };

  const saveRecipe = async () => {
    try {
      await api.put(`/restaurants/${restaurant.id}/recipes`, { item_name: recipeItem, ingredients: recipeIngs });
      toast.success("Recipe saved — stock will deduct on each sale");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const lowStock = ingredients.filter((i) => i.stock_level <= i.par_level);

  return (
    <div data-testid="inventory-tab" className="space-y-6">
      {lowStock.length > 0 && (
        <div className="rounded-2xl border border-amber-400/40 bg-amber-400/5 p-5" data-testid="low-stock-alerts">
          <p className="text-sm font-medium flex items-center gap-2 text-amber-500">
            <AlertTriangle className="w-4 h-4" /> Low stock — reorder needed
          </p>
          <div className="flex flex-wrap gap-2 mt-3">
            {lowStock.map((i) => (
              <Badge key={i.id} variant="outline" className="text-amber-500 border-amber-400/40">
                {i.name}: {i.stock_level}{i.unit} (par {i.par_level})
              </Badge>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-border/60 bg-card p-6">
          <h3 className="font-outfit font-medium text-lg flex items-center gap-2"><Package className="w-5 h-5 text-primary" /> Ingredients</h3>
          <div className="grid grid-cols-12 gap-2 mt-4">
            <Input data-testid="ing-name" placeholder="Name" value={ingForm.name}
              onChange={(e) => setIngForm({ ...ingForm, name: e.target.value })} className="bg-background border-border/60 col-span-4 h-9 text-sm" />
            <Input data-testid="ing-unit" placeholder="Unit (g/ml/pcs)" value={ingForm.unit}
              onChange={(e) => setIngForm({ ...ingForm, unit: e.target.value })} className="bg-background border-border/60 col-span-2 h-9 text-sm" />
            <Input data-testid="ing-stock" type="number" placeholder="Stock" value={ingForm.stock_level}
              onChange={(e) => setIngForm({ ...ingForm, stock_level: e.target.value })} className="bg-background border-border/60 col-span-2 h-9 text-sm" />
            <Input data-testid="ing-par" type="number" placeholder="Par" value={ingForm.par_level}
              onChange={(e) => setIngForm({ ...ingForm, par_level: e.target.value })} className="bg-background border-border/60 col-span-2 h-9 text-sm" />
            <Input data-testid="ing-cost" type="number" step="0.01" placeholder="£/unit" value={ingForm.cost_per_unit}
              onChange={(e) => setIngForm({ ...ingForm, cost_per_unit: e.target.value })} className="bg-background border-border/60 col-span-2 h-9 text-sm" />
          </div>
          <Button data-testid="ing-add-btn" onClick={addIngredient} disabled={!ingForm.name} size="sm" className="rounded-full mt-3">
            <Plus className="w-4 h-4 mr-1" /> Add ingredient
          </Button>
          <div className="mt-4 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border/60">
                  <TableHead>Ingredient</TableHead><TableHead>Stock</TableHead><TableHead>Par</TableHead><TableHead>£/unit</TableHead><TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ingredients.map((i) => (
                  <TableRow key={i.id} data-testid={`ing-row-${i.id}`} className="border-border/40">
                    <TableCell className="font-medium">{i.name}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <button data-testid={`stock-dec-${i.id}`} onClick={() => adjustStock(i.id, -1)} className="w-6 h-6 rounded border border-border/60 text-muted-foreground hover:text-foreground">−</button>
                        <span className={`text-sm w-16 text-center ${i.stock_level <= i.par_level ? "text-amber-500 font-medium" : ""}`}>{i.stock_level}{i.unit}</span>
                        <button data-testid={`stock-inc-${i.id}`} onClick={() => adjustStock(i.id, 1)} className="w-6 h-6 rounded border border-border/60 text-muted-foreground hover:text-foreground">+</button>
                      </div>
                    </TableCell>
                    <TableCell>{i.par_level}{i.unit}</TableCell>
                    <TableCell>£{i.cost_per_unit.toFixed(2)}</TableCell>
                    <TableCell>
                      <button data-testid={`ing-del-${i.id}`} onClick={() => removeIngredient(i.id)}
                        className="text-muted-foreground hover:text-destructive transition-colors"><Trash2 className="w-4 h-4" /></button>
                    </TableCell>
                  </TableRow>
                ))}
                {ingredients.length === 0 && (
                  <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground" data-testid="no-ingredients-msg">No ingredients yet.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        <div className="rounded-2xl border border-border/60 bg-card p-6">
          <h3 className="font-outfit font-medium text-lg">Recipes & food cost</h3>
          <p className="text-xs text-muted-foreground mt-1">Link menu items to ingredients — stock deducts automatically when dishes are served.</p>
          <Select value={recipeItem} onValueChange={loadRecipe}>
            <SelectTrigger data-testid="recipe-item-select" className="mt-4 bg-background border-border/60">
              <SelectValue placeholder="Choose a menu item..." />
            </SelectTrigger>
            <SelectContent>
              {menuItems.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
            </SelectContent>
          </Select>
          {recipeItem && (
            <div className="mt-4 space-y-2.5" data-testid="recipe-editor">
              {recipeIngs.map((ri, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Select value={ri.ingredient_id} onValueChange={(v) => setRecipeIngs(recipeIngs.map((x, i) => (i === idx ? { ...x, ingredient_id: v } : x)))}>
                    <SelectTrigger data-testid={`recipe-ing-${idx}`} className="bg-background border-border/60 h-9 text-sm">
                      <SelectValue placeholder="Ingredient" />
                    </SelectTrigger>
                    <SelectContent>
                      {ingredients.map((i) => <SelectItem key={i.id} value={i.id}>{i.name} ({i.unit})</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input data-testid={`recipe-qty-${idx}`} type="number" step="0.01" placeholder="Qty" value={ri.qty}
                    onChange={(e) => setRecipeIngs(recipeIngs.map((x, i) => (i === idx ? { ...x, qty: Number(e.target.value) } : x)))}
                    className="bg-background border-border/60 h-9 w-24 text-sm" />
                  <button data-testid={`recipe-ing-del-${idx}`} onClick={() => setRecipeIngs(recipeIngs.filter((_, i) => i !== idx))}
                    className="text-muted-foreground hover:text-destructive transition-colors"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
              <div className="flex gap-2">
                <Button data-testid="recipe-add-ing" size="sm" variant="outline" className="rounded-full"
                  onClick={() => setRecipeIngs([...recipeIngs, { ingredient_id: "", qty: 1 }])}>
                  <Plus className="w-3.5 h-3.5 mr-1" /> Ingredient
                </Button>
                <Button data-testid="recipe-save-btn" size="sm" className="rounded-full" onClick={saveRecipe}>Save recipe</Button>
              </div>
            </div>
          )}
          <div className="mt-6 space-y-2.5">
            {recipes.map((r) => (
              <div key={r.item_name} data-testid={`recipe-row-${r.item_name.replace(/\s+/g, "-")}`}
                className="flex items-center justify-between rounded-xl border border-border/40 px-4 py-3">
                <p className="text-sm font-medium">{r.item_name}</p>
                <div className="text-xs text-muted-foreground text-right">
                  <p>cost £{r.food_cost.toFixed(2)}{r.menu_price ? ` / £${r.menu_price.toFixed(2)}` : ""}</p>
                  {r.margin_pct !== null && r.margin_pct !== undefined && (
                    <p className={r.margin_pct >= 60 ? "text-emerald-500" : "text-amber-500"}>{r.margin_pct}% margin</p>
                  )}
                </div>
              </div>
            ))}
            {recipes.length === 0 && <p className="text-sm text-muted-foreground" data-testid="no-recipes-msg">No recipes mapped yet.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
